import React from "react";
import PropTypes from "prop-types";
import "./ErrorConsolePanel.css";

/**
 * Formats a Date into HH:MM:SS.
 */
function formatTime(date) {
  return date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * ErrorConsolePanel
 *
 * Floating badge + expandable panel that displays collected
 * API / GraphQL / JS errors during development.
 */
const ErrorConsolePanel = ({ errors, isOpen, onToggle, onClear }) => {
  const errorCount = errors.filter((e) => e.type === "error").length;
  const warningCount = errors.filter((e) => e.type === "warning").length;
  const networkCount = errors.filter((e) => e.type === "network").length;

  if (errors.length === 0) {
    return null;
  }

  return (
    <>
      {/* ── Floating badge ── */}
      <button
        type="button"
        className="error-console-badge"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-label={`${errorCount} errors, ${warningCount} warnings, ${networkCount} network. Click to ${
          isOpen ? "close" : "open"
        } error console.`}
        data-testid="error-console-badge"
      >
        <span className="error-console-badge__section error-console-badge__section--error">
          <span className="error-console-badge__icon">!</span>
          <span data-testid="error-count">{errorCount}</span>
        </span>
        <span className="error-console-badge__section error-console-badge__section--warning">
          <span className="error-console-badge__icon">⚠</span>
          <span data-testid="warning-count">{warningCount}</span>
        </span>
        <span className="error-console-badge__section error-console-badge__section--network">
          <span className="error-console-badge__icon">🌐</span>
          <span data-testid="network-count">{networkCount}</span>
        </span>
      </button>

      {/* ── Overlay + Panel ── */}
      {isOpen && (
        <>
          <button
            type="button"
            className="error-console-overlay"
            onClick={onToggle}
            aria-label="Close error console"
            data-testid="error-console-overlay"
          />
          <dialog
            open
            className="error-console-panel"
            onCancel={(e) => {
              e.preventDefault();
              onToggle();
            }}
            aria-label="Error console"
            data-testid="error-console-panel"
          >
            <div className="error-console-panel__header">
              <span className="error-console-panel__title">
                🛑 Error Console — {errors.length} issue
                {errors.length === 1 ? "" : "s"}
              </span>
              <div className="error-console-panel__actions">
                <button
                  className="error-console-panel__btn"
                  onClick={onClear}
                  data-testid="error-console-clear"
                >
                  Clear All
                </button>
                <button
                  className="error-console-panel__btn error-console-panel__btn--close"
                  onClick={onToggle}
                  data-testid="error-console-close"
                >
                  Close
                </button>
              </div>
            </div>

            <ul className="error-console-panel__list">
              {errors.map((entry) => (
                <li key={entry.id} className="error-console-entry">
                  <span
                    className={`error-console-entry__tag error-console-entry__tag--${entry.type}`}
                  >
                    {entry.type}
                  </span>
                  <span className="error-console-entry__message">
                    {entry.message}
                  </span>
                  <span className="error-console-entry__time">
                    {formatTime(entry.timestamp)}
                  </span>
                </li>
              ))}
            </ul>
          </dialog>
        </>
      )}
    </>
  );
};

ErrorConsolePanel.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      message: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["error", "warning", "network"]).isRequired,
      timestamp: PropTypes.instanceOf(Date).isRequired,
    })
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default ErrorConsolePanel;
